import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil, tap, switchMap, startWith, filter, take } from 'rxjs/operators';
import { MoneyValidator } from 'src/app/shared/validators/money.validator';
import { GreaterThanZeroValidator } from 'src/app/shared/validators/greater-than-zero.validators';
import { FinancialtransactionsService } from 'src/app/features/financial-transactions/services/financial-transactions.service';
import { AppState } from 'src/app/app.states';
import { Store } from '@ngrx/store';

import { UsersSelectors } from 'src/app/features/users/state/users.selector-types';
import { InvoiceService } from '../../../services/invoice.service';

@Component({
  selector: 'app-edit-investment-dialog',
  templateUrl: './edit-investment-dialog.component.html',
  styleUrls: ['./edit-investment-dialog.component.scss'],
})
export class EditInvestmentDialogComponent implements OnInit, OnDestroy {
  form: FormGroup;
  ngUnsubscribe: Subject<void> = new Subject<void>();
  formSubmitSubject$ = new Subject();

  // Invoice data
  availableAmount: number = 0;
  availableAmountHelper: number = 0;
  projectedAvailableAmount: number = 0;
  invoice: any;
  currency: string = 'pen';

  // Original investment data
  originalAmount: number = 0;
  originalDate: string;

  // Investor info
  user: any;
  userStatus = '';
  idType = '';
  penBalance: string;
  usdBalance: string;

  constructor(
    private store: Store<AppState>,
    public dialogRef: MatDialogRef<EditInvestmentDialogComponent>,
    private formBuilder: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private financialService: FinancialtransactionsService,
    private invoiceService: InvoiceService
  ) {}

  ngOnInit(): void {
    const entity = this.data.entity;

    this.user = entity.user;
    this.originalAmount = entity.amount;
    this.originalDate = entity.investmentDate;
    this.currency = entity.currency || 'pen';

    this.store
      .select(UsersSelectors.profileProperty('status'))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(status => {
        this.userStatus = status;
      });

    this.store
      .select(UsersSelectors.profileProperty('idType'))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(idType => {
        this.idType = idType;
      });

    this.penBalance = undefined;
    this.usdBalance = undefined;
    this.financialService.getInvestorAvailableBalance(entity.user._id).subscribe(balance => {
      this.penBalance = balance.pen.toString();
      this.usdBalance = balance.usd.toString();
      this.form?.get('amount')?.updateValueAndValidity();
    });

    const invoiceId = entity.invoice?._id || entity.invoice;
    this.invoiceService.getInvoiceBalance(invoiceId).subscribe(invoice => {
      this.invoice = invoice;
      this.availableAmount = invoice.availableBalanceAmount;
      this.availableAmountHelper = invoice.availableBalanceAmount;
      this.updateProjectedAvailableAmount(this.originalAmount);
      this.form?.get('amount')?.updateValueAndValidity();
    });

    this.form = this.formBuilder.group({
      amount: [
        entity.amount,
        [Validators.required, GreaterThanZeroValidator, MoneyValidator(1, 2), this.isSufficient()],
      ],
      investmentDate: [entity.investmentDate, [Validators.required]],
    });

    // Subscribe to form value changes to update projected available amount
    this.form.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(val => {
      const newAmount = parseFloat(val.amount) || 0;
      this.updateProjectedAvailableAmount(newAmount);
    });

    // Handle form submission with proper validation
    this.formSubmitSubject$
      .pipe(
        tap(() => {
          this.form.markAllAsTouched();
          Object.keys(this.form.controls).forEach(key => {
            this.form.controls[key].updateValueAndValidity();
          });
        }),
        switchMap(() =>
          this.form.statusChanges.pipe(
            startWith(this.form.status),
            filter(status => status !== 'PENDING'),
            take(1)
          )
        ),
        filter(status => status === 'VALID')
      )
      .subscribe(() => this.submitForm());
  }

  updateProjectedAvailableAmount(newAmount: number): void {
    const difference = newAmount - this.originalAmount;
    this.projectedAvailableAmount = this.availableAmountHelper - difference;
  }

  submit() {
    this.formSubmitSubject$.next();
  }

  submitForm() {
    const editedData = {
      transactionId: this.data.entity._id,
      type: this.data.entity.type,
      currency: this.data.entity.currency,
      status: this.data.entity.status,
      originalAmount: this.originalAmount,
      amount: this.form.get('amount').value,
      investmentDate: this.form.get('investmentDate').value,
      invoice: this.invoice,
      user: this.user,
    };

    this.dialogRef.close({ data: editedData });
  }

  cancel() {
    this.dialogRef.close();
  }

  isSufficient(): ValidatorFn {
    return (control: AbstractControl) => {
      try {
        const newAmount = parseFloat(control.value);

        if (isNaN(newAmount)) {
          return null;
        }

        // Check minimum investment amount based on currency
        if (this.currency === 'pen' && newAmount < 100) {
          return { minimumInvestmentAllowedis100: true };
        }

        if (this.currency === 'usd' && newAmount < 33) {
          return { minimumInvestmentAllowedis33: true };
        }

        // Calculate the difference between new and original amount
        const difference = newAmount - this.originalAmount;

        // If decreasing or same investment, no balance validation needed
        if (difference <= 0) {
          return null;
        }

        // Data not loaded yet, skip validation
        if (this.penBalance === undefined || this.usdBalance === undefined || !this.invoice) {
          return null;
        }

        // Check if investor has sufficient balance for the increase
        const investorBalance = this.currency === 'pen' ? parseFloat(this.penBalance) : parseFloat(this.usdBalance);

        if (difference > investorBalance) {
          return { sufficientBalance: true };
        }

        // Check if invoice has sufficient available amount for the increase
        if (difference > this.availableAmount) {
          return { invoiceSufficientBalance: true };
        }

        return null;
      } catch (error) {
        return null;
      }
    };
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
